import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjectDetailDTO, TaskDTO, WorkflowDTO, WorkflowState } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Field, FilterPill, Panel, inputClass, toneColor, type Tone } from "@/design/primitives";
import { canContribute, useProjectRole } from "../project-data";

/**
 * Kolik karet si sloupec bere na jedno nadechnutí. Deska se stovkami tasků
 * se dřív načítala celá naráz (a s osmi relacemi na task) — proto se dnes
 * ptáme po sloupcích a po stránkách, dokud uživatel neodroluje níž.
 */
const PAGE_SIZE = 20;

/** Klíč dotazu drží prefix ["section", id, "tasks"], aby ho realtime i mutace zneplatnily spolu se zbytkem desky. */
const columnKey = (projectId: string, dept: string | null, column: string) =>
  ["section", projectId, "tasks", "kanban", dept ?? "all", column] as const;
const countsKey = (projectId: string, dept: string | null) =>
  ["section", projectId, "tasks", "counts", dept ?? "all"] as const;

/** KANBAN dle /Design: dept chips filtr, sloupce z workflow, EP na kartách. */
export function KanbanTab({ project }: { project: ProjectDetailDTO }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useProjectRole(project);
  const departments = useQuery({
    queryKey: ["section", project.id, "departments"],
    queryFn: () => api.projectDepartments(project.id),
  });
  const workflows = useQuery({ queryKey: ["workflows"], queryFn: api.workflows });
  // filtr oddělení drží adresa (`?dept=`), ne komponenta
  const search = useSearch({ strict: false }) as { dept?: string };
  const deptFilter = search.dept ?? null;
  const setDeptFilter = (next: string | null) =>
    void navigate({
      to: "/projects/$slug/$tab",
      params: { slug: project.slug, tab: "kanban" },
      search: next ? { dept: next } : {},
    });
  const [creating, setCreating] = useState(false);
  const [dragTaskId, setDragTaskId] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  const deptList = departments.data?.departments ?? [];
  const activeDept = deptList.find((d) => d.id === deptFilter) ?? null;
  const workflowKey = activeDept?.workflowKey ?? (deptFilter === null ? null : "simple");

  // bez filtru: sloupce development workflow (nejbohatší); tasky mimo něj se mapují dle klíče stavu
  const workflow: WorkflowDTO | null = useMemo(() => {
    const list = workflows.data?.workflows ?? [];
    if (workflowKey) return list.find((w) => w.key === workflowKey) ?? null;
    return list.find((w) => w.key === "development") ?? list[0] ?? null;
  }, [workflows.data, workflowKey]);

  const counts = useQuery({
    queryKey: countsKey(project.id, deptFilter),
    queryFn: () => api.taskCounts(project.id, deptFilter ?? undefined),
  });

  const moveMutation = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      api.moveTask(input.id, { status: input.status }),
    onSuccess: () => {
      setMoveError(null);
      void queryClient.invalidateQueries({ queryKey: ["section", project.id, "tasks"] });
    },
    onError: (err) => setMoveError(err instanceof Error ? err.message : cs.common.error),
  });

  const workflowList = workflows.data?.workflows ?? [];
  const columns = useMemo(
    () => workflow?.states.filter((s) => s.kind !== "FAILED") ?? [],
    [workflow],
  );

  /**
   * Které stavy patří do kterého sloupce. Dřív se to počítalo z načtených
   * tasků — se stránkováním by pohled „VŠE" mapoval jen to, co je zrovna
   * vidět. Mapa proto vzniká ze samotných workflow: cizí stav padne na
   * poslední sloupec stejného druhu (TESTING → IN PROGRESS, jde o běžící práci).
   */
  const { statusesByColumn, foreignState } = useMemo(() => {
    const byColumn = new Map<string, string[]>(columns.map((c) => [c.key, [c.key]]));
    const foreign = new Map<string, { state: WorkflowState; workflowName: string }>();
    for (const wf of workflowList) {
      for (const state of wf.states) {
        if (state.kind === "FAILED") continue;
        if (columns.some((c) => c.key === state.key)) continue;
        const target = columns.filter((c) => c.kind === state.kind).at(-1);
        if (!target) continue;
        const list = byColumn.get(target.key)!;
        if (!list.includes(state.key)) list.push(state.key);
        if (!foreign.has(state.key)) foreign.set(state.key, { state, workflowName: wf.name });
      }
    }
    return { statusesByColumn: byColumn, foreignState: foreign };
  }, [columns, workflowList]);

  const countMap = counts.data?.counts ?? {};
  const placed = new Set([...statusesByColumn.values()].flat());
  const extraStatuses = Object.keys(countMap).filter((s) => !placed.has(s) && countMap[s]! > 0);

  if (!workflow) return null;

  return (
    <div className="mt-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="af-label-dim mr-1">{cs.kanban.department}</span>
        <FilterChip label={cs.kanban.all} active={deptFilter === null} onClick={() => setDeptFilter(null)} />
        {deptList.map((d) => (
          <FilterChip
            key={d.id}
            label={d.name.toUpperCase()}
            tone={(d.color as Tone) ?? "cyan"}
            active={deptFilter === d.id}
            onClick={() => setDeptFilter(d.id)}
          />
        ))}
        <span className="ml-auto text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.kanban.workflowNote(workflow.name.toUpperCase())}
        </span>
        {canContribute(role) ? (
          <Btn onClick={() => setCreating(true)} data-testid="kanban-new-task">
            {cs.kanban.newTask}
          </Btn>
        ) : null}
      </div>

      {moveError ? (
        <div className="mb-2 text-[12px]" style={{ color: "var(--critical)" }} data-testid="kanban-error">
          ⚠ {moveError}
        </div>
      ) : null}

      {creating ? (
        <NewTaskForm
          project={project}
          departments={deptList}
          defaultDepartmentId={deptFilter}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {/* mobil/tablet: horizontální scroll sloupců; xl: původní rovnoměrný grid */}
      <div className="overflow-x-auto">
        <div
          className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] items-start gap-3 xl:grid-flow-row xl:grid-cols-[repeat(var(--kanban-cols),minmax(0,1fr))]"
          style={{ "--kanban-cols": String(columns.length) } as CSSProperties}
        >
        {columns.map((col) => {
          const statuses = statusesByColumn.get(col.key) ?? [col.key];
          return (
            <KanbanColumn
              key={col.key}
              projectId={project.id}
              projectSlug={project.slug}
              departmentId={deptFilter}
              column={col}
              statuses={statuses}
              total={statuses.reduce((sum, s) => sum + (countMap[s] ?? 0), 0)}
              foreignState={foreignState}
              canDrag={canContribute(role)}
              onDragStart={setDragTaskId}
              onDrop={() => {
                if (dragTaskId) moveMutation.mutate({ id: dragTaskId, status: col.key });
                setDragTaskId(null);
              }}
              onOpen={(number) =>
                void navigate({
                  to: "/projects/$slug/tasks/$number",
                  params: { slug: project.slug, number: String(number) },
                })
              }
            />
          );
        })}
        </div>
      </div>

      {extraStatuses.length > 0 ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          Mimo zobrazené workflow: {extraStatuses.map((s) => `${s} (${countMap[s] ?? 0})`).join(" · ")}
          {deptFilter === null ? " — přepni filtr oddělení" : ""}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Jeden sloupec desky: vlastní scroll, vlastní stránkování.
 *
 * Sloupec je samostatná komponenta schválně — dotaz na jeho stránky je hook
 * a sloupců je proměnlivý počet. Dotahuje se, až když se strážce dostane do
 * zorného pole (infinity scroll), ne po kliknutí na „další".
 */
function KanbanColumn({
  projectId,
  departmentId,
  column,
  statuses,
  total,
  foreignState,
  canDrag,
  onDragStart,
  onDrop,
  onOpen,
}: {
  projectId: string;
  projectSlug: string;
  departmentId: string | null;
  column: WorkflowState;
  statuses: string[];
  total: number;
  foreignState: Map<string, { state: WorkflowState; workflowName: string }>;
  canDrag: boolean;
  onDragStart: (taskId: string) => void;
  onDrop: () => void;
  onOpen: (number: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const query = useInfiniteQuery({
    queryKey: columnKey(projectId, departmentId, column.key),
    queryFn: ({ pageParam }) =>
      api.tasks(projectId, {
        departmentId: departmentId ?? undefined,
        status: statuses,
        limit: PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const tasks = query.data?.pages.flatMap((p) => p.tasks) ?? [];
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      // scroll je uvnitř sloupce, ne v okně — pozorovat se musí ten kontejner
      { root: scrollRef.current, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <div
      className="flex min-h-[220px] flex-col gap-2.5 md:min-h-[420px]"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      data-testid={`kanban-col-${column.key}`}
    >
      {/* sloupec nemá rámeček — nese ho karta; hlavička je jen popisek (p2b) */}
      <div className="af-mono flex justify-between px-1 text-[10px] tracking-[0.14em]">
        <span style={{ color: toneColor[column.tone as Tone] }}>{column.label}</span>
        <span style={{ color: "var(--text-tertiary)" }} data-testid={`kanban-count-${column.key}`}>
          {total}
        </span>
      </div>

      <div
        ref={scrollRef}
        className="flex max-h-[62vh] flex-col gap-2.5 overflow-y-auto pr-1"
        data-testid={`kanban-scroll-${column.key}`}
      >
        {tasks.map((task) => {
          const foreign = foreignState.get(task.status);
          return (
            <TaskCard
              key={task.id}
              task={task}
              draggable={canDrag && !foreign}
              onDragStart={() => onDragStart(task.id)}
              statusBadge={
                foreign
                  ? {
                      label: foreign.state.label,
                      tone: foreign.state.tone as Tone,
                      title: cs.kanban.foreignState(foreign.workflowName),
                    }
                  : undefined
              }
              onOpen={() => onOpen(task.number)}
            />
          );
        })}

        {query.isPending ? (
          <div className="py-4 text-center text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.common.loading}
          </div>
        ) : null}

        {!query.isPending && tasks.length === 0 ? (
          <div className="py-4 text-center text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.kanban.empty}
          </div>
        ) : null}

        {hasNextPage ? (
          <div
            ref={sentinelRef}
            className="py-3 text-center text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
            data-testid={`kanban-more-${column.key}`}
          >
            {isFetchingNextPage ? cs.common.loading : `+ ${total - tasks.length}`}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
  tone = "cyan",
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone?: Tone;
}) {
  return (
    <FilterPill active={active} onClick={onClick} tone={tone}>
      {label}
    </FilterPill>
  );
}

export function TaskCard({
  task,
  onOpen,
  draggable = false,
  onDragStart,
  statusBadge,
}: {
  task: TaskDTO;
  onOpen: () => void;
  draggable?: boolean;
  onDragStart?: () => void;
  /** Skutečný stav tasku z jiného workflow, když je karta zobrazená v mapovaném sloupci. */
  statusBadge?: { label: string; tone: Tone; title?: string };
}) {
  const deptTone = (task.departmentColor as Tone) ?? "cyan";
  const epTone: Tone = task.pinnedAt ? "amber" : task.epScore >= 70 ? "red" : task.epScore >= 50 ? "amber" : "cyan";
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onOpen}
      className="cursor-pointer p-3 transition-colors"
      style={{
        borderRadius: "var(--radius-box)",
        border: `1px solid ${task.pinnedAt ? "color-mix(in srgb, var(--warning) 50%, transparent)" : "var(--border-default)"}`,
        background: task.pinnedAt ? "var(--warning-subtle)" : "var(--surface)",
      }}
      data-testid="task-card"
    >
      <div className="af-mono flex items-center justify-between text-[10px]">
        <span className="flex items-center gap-1.5">
          {task.departmentName ? (
            <span style={{ color: toneColor[deptTone] }}>{task.departmentName.toUpperCase()}</span>
          ) : null}
          {statusBadge ? (
            <span
              style={{ color: toneColor[statusBadge.tone] }}
              title={statusBadge.title}
              data-testid="card-status-badge"
            >
              · {statusBadge.label}
            </span>
          ) : null}
        </span>
        <span className="font-bold" style={{ color: toneColor[epTone] }}>
          EP {task.epScore}
        </span>
      </div>
      <div
        className="mb-[3px] mt-[5px] text-[12px] font-semibold leading-snug"
        style={{ color: "var(--text-primary)" }}
      >
        #{task.number} {task.title}
      </div>
      <div className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
        {task.priority}
        {task.milestoneId ? " · milestone" : ""}
        {task.codebases.length ? ` · ${task.codebases.map((c) => c.slug).join(", ")}` : ""}
        {task.pinnedAt ? " · ⚡ OVERRIDE" : ""}
      </div>
      {task.reworkCount > 0 && task.status !== "DONE" ? (
        <div className="mt-1 text-[11px]" style={{ color: "var(--critical)" }} data-testid="card-rework">
          ↺ REWORK ×{task.reworkCount} — QA vrátilo práci
        </div>
      ) : null}
      {task.openDependencyCount > 0 ? (
        <div className="mt-1 text-[11px]" style={{ color: "var(--critical)" }}>
          {cs.kanban.blockedBy(task.openDependencyCount)}
        </div>
      ) : null}
      {task.blockedByDecisionId ? (
        <div className="mt-1 text-[11px]" style={{ color: "var(--warning)" }}>
          {cs.kanban.waitingDecision}
        </div>
      ) : null}
    </div>
  );
}

function NewTaskForm({
  project,
  departments,
  defaultDepartmentId,
  onClose,
}: {
  project: ProjectDetailDTO;
  departments: { id: string; name: string }[];
  defaultDepartmentId: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [departmentId, setDepartmentId] = useState<string>(defaultDepartmentId ?? "");
  const [priority, setPriority] = useState<"P0" | "P1" | "P2" | "P3">("P2");
  const [acceptance, setAcceptance] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createTask(project.id, {
        title,
        departmentId: departmentId || null,
        priority,
        acceptance: acceptance
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        description: "",
        applicationIds: [],
        codebaseIds: [],
        origin: "SCOPE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["section", project.id, "tasks"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <Panel brackets className="mb-3 max-w-2xl p-4" data-testid="task-new-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_180px_120px]">
        <Field label={cs.fields.name}>
          <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus data-testid="task-new-title" />
        </Field>
        <Field label={cs.fields.department}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            data-testid="task-new-dept"
          >
            <option value="">bez oddělení</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={cs.fields.priority}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={priority}
            onChange={(e) => setPriority(e.target.value as typeof priority)}
          >
            {(["P0", "P1", "P2", "P3"] as const).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="mt-3">
        <Field label={cs.fields.acceptanceCriteria} hint="bez AC nejde task do READY (spec §27)">
          <textarea
            className={`${inputClass} min-h-[64px] resize-y`}
            value={acceptance}
            onChange={(e) => setAcceptance(e.target.value)}
            data-testid="task-new-ac"
          />
        </Field>
      </div>
      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Btn onClick={onClose}>{cs.common.cancel}</Btn>
        <Btn
          variant="primary"
          disabled={title.trim().length < 3 || mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="task-new-save"
        >
          {cs.common.create}
        </Btn>
      </div>
    </Panel>
  );
}
